import * as React from 'react';
import { connect } from 'react-redux';
import { provideIntlService, provideLocalizationService, registerForLocalization, registerForIntl } from '@progress/kendo-react-intl';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { OverviewPage, StackLayout, Text } from '../../../ui';
import { DateTimeFormatter, InlineBadge, ValueItem } from '../../shared/components';
import { getGeneralCompetencyStatusThemeColor } from '../../../resources/generalCompetencyStatus';
import { loadMyClaimOverviewPage, cleanupMyClaimOverviewPage, openOverviewDeleteDialog, closeOverviewDeleteDialog, confirmOverviewDelete } from '../actions/my-claim-overview-page-actions';
import { withRouter } from '../../../components/withRouter';
import { Skeleton } from '@progress/kendo-react-indicators';

class MyClaimOverviewPage extends React.PureComponent {
  constructor() {
    super();
    this.handleBreadcrumbItemClick = this.handleBreadcrumbItemClick.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleDeleteDialogClose = this.handleDeleteDialogClose.bind(this);
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
  }

  async componentDidMount() {
    const { claimId } = this.props.params;
    if (claimId) {
      await this.props.loadMyClaimOverviewPage(claimId);
    }
  }

  componentWillUnmount() {
    this.props.cleanupMyClaimOverviewPage();
  }

  render() {
    const { claim, loading } = this.props;
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    const isPendingReview = claim?.statusCode === 'PendingReview';
    return (
      <>
        <OverviewPage
          title={loading
            ? <Skeleton shape='text' style={{ width: 300, height: 28 }}/>
            : localizationService.toLanguageString('myGeneralCompetencyClaims.overviewTitle')
          }
          breadcrumbItems={[
            {
              id: 'hours',
              text: localizationService.toLanguageString('myGeneralCompetencyHours.listTitle')
            },
            {
              id: 'claim',
              text: localizationService.toLanguageString('myGeneralCompetencyClaims.overviewTitle'),
              disabled: true
            }
          ]}
          onBreadcrumbItemClick={this.handleBreadcrumbItemClick}
          onBackClick={this.handleBackClick}
          headerActions={
            isPendingReview &&
            <>
              <Button themeColor='primary' onClick={this.handleEdit}>
                {localizationService.toLanguageString('buttons.edit')}
              </Button>
              <Button onClick={this.handleDelete}>
                {localizationService.toLanguageString('buttons.delete')}
              </Button>
            </>
          }
          summary={
            <StackLayout rowGap='8px'>
              <ValueItem
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.title')}
                labelWidth='150px'
                loading={loading}
              >
                {claim?.title}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.organizer')}
                labelWidth='150px'
                loading={loading}
              >
                {claim?.organizer}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.occurredAt')}
                labelWidth='150px'
                loading={loading}
              >
                {claim?.occurredAt ? DateTimeFormatter(claim.occurredAt, intlService) : null}
              </ValueItem>
              <ValueItem
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.minutes')}
                labelWidth='150px'
                loading={loading}
              >
                {claim?.minutes}
              </ValueItem>
              {claim?.description &&
                <ValueItem
                  label={localizationService.toLanguageString('myGeneralCompetencyClaims.description')}
                  labelWidth='150px'
                  loading={loading}
                >
                  {claim.description}
                </ValueItem>
              }
              <ValueItem
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.status')}
                labelWidth='150px'
                loading={loading}
              >
                {claim?.statusCode &&
                  <InlineBadge themeColor={getGeneralCompetencyStatusThemeColor(claim.statusCode)}>
                    {this.getLocalizedStatus(claim.statusCode, localizationService)}
                  </InlineBadge>
                }
              </ValueItem>
              {claim?.attachmentFilename &&
                <ValueItem
                  label={localizationService.toLanguageString('myGeneralCompetencyClaims.attachment')}
                  labelWidth='150px'
                  loading={loading}
                >
                  {claim.attachmentFilename}
                </ValueItem>
              }
              {claim?.reviewComment &&
                <ValueItem
                  label={localizationService.toLanguageString('myGeneralCompetencyClaims.reviewComment')}
                  labelWidth='150px'
                  loading={loading}
                >
                  {claim.reviewComment}
                </ValueItem>
              }
              {claim?.reviewedAt &&
                <ValueItem
                  label={localizationService.toLanguageString('myGeneralCompetencyClaims.reviewedAt')}
                  labelWidth='150px'
                  loading={loading}
                >
                  {DateTimeFormatter(claim.reviewedAt, intlService)}
                </ValueItem>
              }
              <ValueItem
                label={localizationService.toLanguageString('myGeneralCompetencyClaims.createdOn')}
                labelWidth='150px'
                loading={loading}
              >
                {claim?.createdOn ? DateTimeFormatter(claim.createdOn, intlService) : null}
              </ValueItem>
            </StackLayout>
          }
        >
          {!loading && this.props.pdfObjectUrl &&
            <iframe
              src={this.props.pdfObjectUrl}
              title={localizationService.toLanguageString('myGeneralCompetencyClaims.attachment')}
              width='100%'
              height='800'
            />
          }
          {!loading && !this.props.pdfObjectUrl && claim &&
            <Text variant='subtitle1'>
              {localizationService.toLanguageString('myGeneralCompetencyClaims.noAttachment')}
            </Text>
          }
        </OverviewPage>
        {this.props.isDeleteDialogVisible &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.handleDeleteDialogClose}>
            <p>{localizationService.toLanguageString('myGeneralCompetencyClaims.deleteClaimConfirm')}</p>
            <DialogActionsBar>
              <Button themeColor='primary' onClick={this.handleDeleteConfirm}>
                {localizationService.toLanguageString('buttons.delete')}
              </Button>
              <Button onClick={this.handleDeleteDialogClose}>
                {localizationService.toLanguageString('buttons.cancel')}
              </Button>
            </DialogActionsBar>
          </Dialog>
        }
      </>
    );
  }

  getLocalizedStatus(status, localizationService) {
    const statusMap = {
      'PendingReview': 'myGeneralCompetencyClaims.statusPendingReview',
      'Approved': 'myGeneralCompetencyClaims.statusApproved',
      'Rejected': 'myGeneralCompetencyClaims.statusRejected'
    };
    const key = statusMap[status];
    return key ? localizationService.toLanguageString(key) : status;
  }

  handleBreadcrumbItemClick(e) {
    if (e.id === 'hours') {
      this.props.navigate('/my_general_competency_hours');
    }
  }

  handleBackClick() {
    this.props.navigate(-1);
  }

  handleEdit() {
    const { claim } = this.props;
    if (claim?.id) {
      this.props.navigate(`/my_general_competency_claims/${claim.id}/edit`);
    }
  }

  handleDelete() {
    this.props.openOverviewDeleteDialog();
  }

  handleDeleteDialogClose() {
    this.props.closeOverviewDeleteDialog();
  }

  handleDeleteConfirm() {
    this.props.confirmOverviewDelete();
  }
}

registerForLocalization(MyClaimOverviewPage);
registerForIntl(MyClaimOverviewPage);

const mapStateToProps = state => ({
  claim: state.myGeneralCompetencyClaimOverviewPage.claim,
  loading: state.myGeneralCompetencyClaimOverviewPage.loading,
  pdfObjectUrl: state.myGeneralCompetencyClaimOverviewPage.pdfObjectUrl,
  isDeleteDialogVisible: state.myGeneralCompetencyClaimOverviewPage.isDeleteDialogVisible
});

const mapDispatchToProps = dispatch => ({
  loadMyClaimOverviewPage: (claimId) => dispatch(loadMyClaimOverviewPage(claimId)),
  cleanupMyClaimOverviewPage: () => dispatch(cleanupMyClaimOverviewPage()),
  openOverviewDeleteDialog: () => dispatch(openOverviewDeleteDialog()),
  closeOverviewDeleteDialog: () => dispatch(closeOverviewDeleteDialog()),
  confirmOverviewDelete: () => dispatch(confirmOverviewDelete())
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MyClaimOverviewPage));
